import {
  Address,
  AddressInput,
  Company,
  CompanySummary,
  CompanyTypes,
  CreateDirectoryCompanyInput,
  EmailAddressInput,
  LicenseInput,
  PartyCompany,
  PartyCompanyInput,
  PhoneNumberInput,
  PhoneNumberType,
} from '../__graphql_generated__';
import { AddressType } from '@fluentsms/types-fluent';

const safeArray = <T>(input: T[] | undefined | null): T[] => input ?? [];

interface WireInstructionsClone {
  receivingBank?: {
    obi?: Array<{ value?: string }>;
    obiLine1?: string;
    obiLine2?: string;
    obiLine3?: string;
    obiLine4?: string;
  };
  beneficiaryBank?: {
    bbi?: Array<{ value?: string }>;
    bbiLine1?: string;
    bbiLine2?: string;
    bbiLine3?: string;
    bbiLine4?: string;
  };
}

const cloneWireInstructions = (wireInstructions: unknown) => {
  if (!wireInstructions) return undefined;

  const cloned = JSON.parse(JSON.stringify(wireInstructions)) as WireInstructionsClone;

  if (cloned.receivingBank?.obi) {
    const obi = cloned.receivingBank.obi;
    cloned.receivingBank.obiLine1 = obi[0]?.value || '';
    cloned.receivingBank.obiLine2 = obi[1]?.value || '';
    cloned.receivingBank.obiLine3 = obi[2]?.value || '';
    cloned.receivingBank.obiLine4 = obi[3]?.value || '';
    delete cloned.receivingBank.obi;
  }

  if (cloned.beneficiaryBank?.bbi) {
    const bbi = cloned.beneficiaryBank.bbi;
    cloned.beneficiaryBank.bbiLine1 = bbi[0]?.value || '';
    cloned.beneficiaryBank.bbiLine2 = bbi[1]?.value || '';
    cloned.beneficiaryBank.bbiLine3 = bbi[2]?.value || '';
    cloned.beneficiaryBank.bbiLine4 = bbi[3]?.value || '';
    delete cloned.beneficiaryBank.bbi;
  }

  return cloned;
};

export const toCompanyInputsV2 = () => {
  const toCompanyTypes = (company: Company | PartyCompany | CompanySummary): string[] => {
    if (!company?.companyTypes) {
      return [];
    }
    return safeArray(company?.companyTypes)
      .flatMap((type: CompanyTypes | string | null) => {
        if (typeof type === 'string') {
          return type;
        }

        if (typeof type === 'object' && type !== null && 'types' in type && Array.isArray(type.types)) {
          return type.types.map((t) => t?.id);
        }

        return [];
      })
      .filter((id): id is string => id !== undefined); // Filter out undefined values
  };

  const toEmailAddressInput = (company: Company | PartyCompany | CompanySummary): EmailAddressInput[] => {
    if ('primaryEmail' in company) {
      return [
        {
          emailAddress: company?.primaryEmail ?? '',
          isPrimary: true,
        },
      ];
    }
    if ('emailAddresses' in company) {
      return safeArray(company.emailAddresses).map((email) => ({
        emailAddress: email?.emailAddress ?? '',
        isPrimary: email?.isPrimary ?? false,
      }));
    }
    return [];
  };

  const toPhoneNumberInput = (company: Company | PartyCompany | CompanySummary): PhoneNumberInput[] => {
    if ('phoneNumbers' in company && Array.isArray(company?.phoneNumbers)) {
      const phoneNumbers = company.phoneNumbers;
      if (phoneNumbers.length === 0) {
        return [];
      } else {
        return phoneNumbers.map((phone) => ({
          type: phone?.type ?? '', // Ensure the type property is included
          phoneNumber: phone?.phoneNumber ?? '',
          ext: phone?.ext ?? undefined,
          isPrimary: phone?.isPrimary ?? undefined,
        }));
      }
    }
    const phoneNumberInput: PhoneNumberInput[] = [];
    if ('primaryPhone' in company) {
      const primaryPhone = createPhoneNumberByType('primaryPhone', company);
      if (primaryPhone?.phoneNumber) {
        phoneNumberInput.push(primaryPhone);
      }
    }

    return phoneNumberInput;
  };

  const toLicenseInput = (company: Company | PartyCompany | CompanySummary): LicenseInput[] => {
    if ('licenses' in company && Array.isArray(company?.licenses)) {
      const licenses = company?.licenses;
      if (licenses.length === 0) {
        return [];
      } else {
        return licenses.map((license) => ({
          licensedState: license?.licensedState,
          licenseNumber: license?.licenseNumber,
        }));
      }
    }
    return [];
  };

  const toAddressInput = (company: Company | PartyCompany | CompanySummary): AddressInput[] => {
    if ('addresses' in company && Array.isArray(company?.addresses)) {
      const addresses = company.addresses;
      if (addresses.length === 0) {
        return [];
      } else {
        return addresses.map((address) => ({
          addressType: normalizeAddressType(address?.addressType),
          address1: address?.address1 ?? undefined,
          address2: address?.address2 ?? undefined,
          city: address?.city ?? undefined,
          countyName: address?.countyName ?? address?.county ?? undefined,
          country: address?.country ?? undefined,
          countyFips: address?.countyFips ?? undefined,
          postalCode: address?.postalCode ?? undefined,
          stateOrProvince: address?.stateOrProvince ?? undefined,
          usePrimaryAddress: address?.usePrimaryAddress ?? undefined,
        }));
      }
    }

    const addressInput: AddressInput[] = [];

    if ('mailingAddress' in company) {
      const mailingAddress = createAddressByType(AddressType.MailingAddress, company);
      if (mailingAddress) {
        addressInput.push(mailingAddress);
      }
    }
    if ('primaryAddress' in company) {
      const primaryAddress = createAddressByType(AddressType.PrimaryAddress, company);
      if (primaryAddress) {
        addressInput.push(primaryAddress);
      }
    }
    return addressInput;
  };

  const toPartyCompany = (
    company: Company | PartyCompany | CompanySummary,
    directoryCompanyId?: string,
  ): PartyCompanyInput => {
    const directoryCompanyReferenceId = () => {
      if ('directoryCompanyReferenceId' in company) {
        return company?.directoryCompanyReferenceId;
      }
      if (directoryCompanyId) {
        return directoryCompanyId;
      }
      // If company does not belong to a directory, return undefined
      if ('id' in company) {
        return company.id;
      }
      return undefined;
    };

    return {
      directoryCompanyReferenceId: directoryCompanyReferenceId(),
      companyName: company?.companyName,
      companyTypes: toCompanyTypes(company),
      alternateSearchName: company?.alternateSearchName,
      emailAddresses: toEmailAddressInput(company),
      phoneNumbers: toPhoneNumberInput(company),
      addresses: toAddressInput(company),
      website: company?.website,
      altaId: 'altaId' in company ? company?.altaId : undefined,
      lenderLanguage:
        'lenderLanguage' in company && company?.lenderLanguage !== null ? company?.lenderLanguage : undefined,
      lenderLanguageDefaultType:
        'lenderLanguageDefaultType' in company && company?.lenderLanguageDefaultType !== null
          ? company?.lenderLanguageDefaultType
          : undefined,
      lenderLanguageOptions:
        'lenderLanguageOptions' in company && company?.lenderLanguageOptions !== null
          ? company?.lenderLanguageOptions
          : undefined,
      licenses: toLicenseInput(company),
      nmlsId: 'nmlsId' in company ? company?.nmlsId : undefined,
      taxId: 'taxId' in company ? company?.taxId : undefined,
      taxIdType: 'taxIdType' in company ? company?.taxIdType : undefined,
      tenantOrgReferenceId: 'tenantOrgReferenceId' in company ? company?.tenantOrgReferenceId : undefined,
      wireInstructions: 'wireInstructions' in company ? cloneWireInstructions(company?.wireInstructions) : undefined,
    };
  };

  const toDirectoryCompany = (company: Company | PartyCompany): CreateDirectoryCompanyInput => {
    const returnData: CreateDirectoryCompanyInput = {
      companyName: company?.companyName,
      alternateSearchName: company?.alternateSearchName ?? undefined,
      taxIdType: company?.taxIdType ?? undefined,
      taxId: company?.taxId ?? undefined,
      altaId: company?.altaId ?? undefined,
      nmlsId: company?.nmlsId ?? undefined,
      website: company?.website ?? undefined,
      wireInstructions: cloneWireInstructions(company?.wireInstructions),
      emailAddresses: toEmailAddressInput(company),
      phoneNumbers: toPhoneNumberInput(company),
      companyTypes: toCompanyTypes(company),
      licenses: toLicenseInput(company),
      addresses: toAddressInput(company),
    };

    // Ensure addresses are included even if empty
    if (!returnData.addresses) {
      returnData.addresses = [];
    }

    // Narrow the type to PartyCompany if 'orgIds' exists
    if ('orgIds' in company) {
      returnData.orgIds = company.orgIds;
    }

    return returnData;
  };

  const toCompanySummary = (company: Company | PartyCompany | CompanySummary): CompanySummary => {
    const primaryEmail = toEmailAddressInput(company).find((email) => email.isPrimary)?.emailAddress || undefined;
    const primaryPhone = toPhoneNumberInput(company).find((phone) => phone.isPrimary) || undefined;
    const mappedAddresses = toAddressInput(company);
    const mailingAddress = mappedAddresses.find((addr) => addr.addressType === AddressType.MailingAddress) || undefined;
    const primaryAddress = mappedAddresses.find((addr) => addr.addressType === AddressType.PrimaryAddress) || undefined;

    return {
      active: 'active' in company ? company.active : undefined,
      alternateSearchName: company.alternateSearchName || undefined,
      companyName: company.companyName || undefined,
      companyTypes: toCompanyTypes(company),
      id: 'id' in company ? company.id : '',
      isManagedEntry: 'isManagedEntry' in company ? company.isManagedEntry : undefined,
      mailingAddress: mailingAddress,
      orgIds: 'orgIds' in company ? company.orgIds : undefined,
      parentItemId: 'parentItemId' in company ? company.parentItemId : undefined,
      primaryAddress: primaryAddress,
      primaryEmail: primaryEmail,
      primaryPhone: primaryPhone,
      tenantOrgIds: 'tenantOrgIds' in company ? company.tenantOrgIds : undefined,
      updatedTimestamp: 'updatedTimestamp' in company ? company.updatedTimestamp : undefined,
      website: company.website || undefined,
    };
  };

  return {
    toPartyCompany,
    toCompanySummary,
    toDirectoryCompany,
    getCompanyTypes: toCompanyTypes,
    getCompanyEmail: toEmailAddressInput,
    getCompanyPhoneNumbers: toPhoneNumberInput,
    getCompanyLicense: toLicenseInput,
    getCompanyAddresses: toAddressInput,
  };
};

const createPhoneNumberByType = (type: 'primaryPhone', company: CompanySummary): PhoneNumberInput | undefined => {
  const phone = company[type] as PhoneNumberType | undefined;
  return {
    type: phone?.type ?? '',
    phoneNumber: phone?.phoneNumber ?? '',
    ext: phone?.ext ?? undefined,
    isPrimary: true,
  };
};

const ADDRESS_TYPE_TO_FIELD_KEY: Partial<Record<AddressType, 'mailingAddress' | 'primaryAddress'>> = {
  [AddressType.PrimaryAddress]: 'primaryAddress',
  [AddressType.MailingAddress]: 'mailingAddress',
};

export const LEGACY_ADDRESS_TYPE_MAP: Record<string, AddressType> = {
  primaryAddress: AddressType.PrimaryAddress,
  mailingAddress: AddressType.MailingAddress,
  postClosingReturnAddress: AddressType.PostClosingReturnAddress,
};

export const normalizeAddressType = (addressType: string | null | undefined): string | undefined => {
  if (!addressType) return undefined;
  return LEGACY_ADDRESS_TYPE_MAP[addressType] ?? addressType;
};

export const normalizeAddress = <T extends { addressType?: string | null | undefined }>(
  address: T | null | undefined,
): T | undefined => {
  if (!address?.addressType) return address ?? undefined;
  const canonical = LEGACY_ADDRESS_TYPE_MAP[address.addressType];
  return canonical ? { ...address, addressType: canonical } : address;
};

export const extractAddressesByType = <T extends { addressType?: string | null | undefined }>(
  addresses: (T | null | undefined)[] | null | undefined,
): { primaryAddress: T | undefined; mailingAddress: T | undefined } => {
  if (!addresses) return { primaryAddress: undefined, mailingAddress: undefined };
  const normalized = addresses.map(normalizeAddress);
  return {
    primaryAddress: normalized.find((a) => a?.addressType === AddressType.PrimaryAddress),
    mailingAddress: normalized.find((a) => a?.addressType === AddressType.MailingAddress),
  };
};

const createAddressByType = (type: AddressType, company: CompanySummary): AddressInput | undefined => {
  const fieldKey = ADDRESS_TYPE_TO_FIELD_KEY[type];
  if (!fieldKey) return undefined;
  const address = company[fieldKey] as Address | undefined;
  return {
    addressType: type,
    address1: address?.address1 ?? undefined,
    address2: address?.address2 ?? undefined,
    city: address?.city ?? undefined,
    countyName: address?.countyName ?? address?.county ?? undefined,
    country: address?.country ?? undefined,
    countyFips: address?.countyFips ?? undefined,
    postalCode: address?.postalCode ?? undefined,
    stateOrProvince: address?.stateOrProvince ?? undefined,
    usePrimaryAddress: type === AddressType.PrimaryAddress,
  };
};
